import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, setAdminToken } from '@/utils/adminApi';
import { setAdminSessionMeta } from '@/utils/adminPermissions';
import {
  notifyIpePluginAdminAuthSuccess,
} from '@/utils/ipePluginBridge';
import AdminPasswordInput from './components/AdminPasswordInput';
import AdminToast from './components/AdminToast';
import ClickCaptchaField from './components/ClickCaptchaField';
import { useAdminToast } from './components/useAdminToast';
import '@/styles/admin/admin.css';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const isPluginEmbed = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('ipe_plugin') === '1';
  const isSilentCheck = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('silent') === '1';
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaReady, setCaptchaReady] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pluginAutoConnected, setPluginAutoConnected] = useState(false);
  const { toast, showSuccess, showError, closeToast } = useAdminToast();

  useEffect(() => {
    document.title = 'IPE · Admin控制台';
    return () => {
      document.title = 'IPE';
    };
  }, []);

  useEffect(() => {
    if (!isPluginEmbed) return;
    try {
      if (localStorage.getItem('ipe_admin_token')) {
        notifyIpePluginAdminAuthSuccess();
        if (!isSilentCheck) setPluginAutoConnected(true);
      }
    } catch {
      /* ignore */
    }
  }, [isPluginEmbed, isSilentCheck]);

  const handleCaptchaVerified = useCallback((token) => {
    setCaptchaToken(token);
    setCaptchaReady(true);
    showSuccess('安全验证已通过，可以进行登录', '验证成功');
  }, [showSuccess]);

  const handleCaptchaError = useCallback((message) => {
    setCaptchaToken('');
    setCaptchaReady(false);
    showError(message, '人机验证');
  }, [showError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaReady || !captchaToken) {
      showError('请先完成安全验证后再登录', '安全验证');
      return;
    }
    setLoading(true);
    try {
      const data = await adminAPI.login(phone, password, captchaToken);
      setAdminToken(data.token);
      setAdminSessionMeta(data.role, data.permissions);
      if (isPluginEmbed) {
        notifyIpePluginAdminAuthSuccess();
        return;
      }
      navigate('/ipe/admin/dashboard', { replace: true });
    } catch (err) {
      setCaptchaToken('');
      setCaptchaReady(false);
      setCaptchaKey((k) => k + 1);
      showError(err.message || '登录失败', '登录失败');
    } finally {
      setLoading(false);
    }
  };

  if (isPluginEmbed && (isSilentCheck || pluginAutoConnected)) {
    return (
      <div className="admin-login admin-login--plugin-embed">
        <p className="ipe-login-page__loading-text">
          {pluginAutoConnected ? '已登录 Admin，正在连接插件…' : '正在同步 Admin 登录状态…'}
        </p>
      </div>
    );
  }

  return (
    <div className={`admin-login${isPluginEmbed ? ' admin-login--plugin-embed' : ''}`}>
      <div className="admin-login__bg" />
      <div className="admin-login__grid" />
      <div className="admin-login__orb admin-login__orb--1" />
      <div className="admin-login__orb admin-login__orb--2" />

      <div className="admin-login__container">
        <div className="admin-login__brand">
          <div className="admin-login__logo">IP</div>
          <h1>IPE admin控制台</h1>
          <p>智能运营控制台 · 统一管理入口</p>
        </div>

        <form className="admin-login__card" onSubmit={handleSubmit}>
          <label>
            管理员手机号
            <input
              className="admin-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="请输入 11 位手机号"
              autoComplete="username"
              maxLength={11}
            />
          </label>
          <label>
            登录密码
            <AdminPasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入密码"
            />
          </label>
          <div className="admin-login__captcha">
            <span className="admin-login__captcha-label">安全验证</span>
            <ClickCaptchaField
              key={captchaKey}
              scene="admin-login"
              verified={captchaReady}
              onVerified={handleCaptchaVerified}
              onError={handleCaptchaError}
              disabled={loading}
            />
          </div>
          <button type="submit" className="admin-login__submit" disabled={loading}>
            {loading ? '验证中…' : '安全登录'}
          </button>
        </form>

        <p className="admin-login__footer">IPE admin控制台 · Secure Access</p>
      </div>
      <AdminToast toast={toast} onClose={closeToast} />
    </div>
  );
}
